import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
} from "@mui/material";
import { LocationOn, Email, Phone } from "@mui/icons-material";
import { useAppSelector } from "../redux/store";
import Logo from "../components/Logo";
import { BranchResponseDto } from "../shared/dtos/branch/BranchResponseDto";

const TenantHomePage: React.FC = () => {
  const { company } = useAppSelector((state) => state.company);
  const activeBranches: BranchResponseDto[] =
    company?.branches?.filter((branch) => branch.status === "ACTIVE") || [];

  if (!company) return null;

  return (
    <Box sx={{ pb: 8, bgcolor: "background.default" }}>
      {/* HERO SECTION */}
      <Paper
        elevation={0}
        sx={{
          background: "linear-gradient(135deg, #4254be 0%, #2f3c83 100%)",
          color: "white",
          py: 8,
          mb: 4,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            <Logo size="md" />
            <Box>
              <Typography
                variant="h2"
                component="h1"
                sx={{ fontWeight: "bold", mb: 1 }}
              >
                Welcome to {company.name}
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9 }}>
                Company Code: {company.companyCode}
              </Typography>
            </Box>
          </Box>
        </Container>
      </Paper>

      <Container maxWidth="lg">
        {/* CONTACT & BRANCHES */}
        <Grid container spacing={4}>
          {/* LEFT: CONTACT INFO */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Typography variant="h4" sx={{ mb: 3 }}>
              Contact Information
            </Typography>
            <Card>
              <CardContent>
                <List>
                  <ListItem disableGutters>
                    <ListItemIcon>
                      <LocationOn color="secondary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Head Office Address"
                      secondary={`${company.headOfficeAddress.streetLine1}, ${company.headOfficeAddress.city}`}
                    />
                  </ListItem>
                  <ListItem disableGutters>
                    <ListItemIcon>
                      <Email color="secondary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Email"
                      secondary={company.contactEmail}
                    />
                  </ListItem>
                  <ListItem disableGutters>
                    <ListItemIcon>
                      <Phone color="secondary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Phone"
                      secondary={company.contactPhone}
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* RIGHT: BRANCHES */}
          <Grid size={{ xs: 12, md: 7 }}>
            <Typography variant="h4" sx={{ mb: 3 }}>
              Our Branches
            </Typography>
            <Grid container spacing={2}>
              {activeBranches.map((branch) => (
                <Grid size={{ xs: 12, sm: 6 }} key={branch.branchCode}>
                  <Card sx={{ height: "100%" }}>
                    <CardContent>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                        }}
                      >
                        <Typography variant="h6">{branch.name}</Typography>
                        <Chip
                          label={branch.status}
                          color="success"
                          size="small"
                        />
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        Code: {branch.branchCode}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {branch.address.city}, {branch.address.country}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default TenantHomePage;
